/**
 * „Liegt bei" – eine Person, eine Rolle, überall dasselbe Bild.
 *
 * Mein Tag, die Gesamtlisten, der Vorgang und die Bestellkarte zeigen alle
 * denselben Sachverhalt: Auf wessen Tisch liegt das gerade, und warum – die
 * Rolle laut Zuständigkeitsmatrix oder eine ausdrückliche Übergabe. Eine
 * Komponente, damit es nirgends zwei Umsetzungen gibt.
 *
 * Mit `onUebergeben` wird das Ganze ein Knopf: Er öffnet den Dialog, in dem
 * der Schritt mit Begründung an jemand anderen geht (Richtlinie 21).
 */

import type { LiegtBei as LiegtBeiTyp } from '../typen';
import './liegtbei.css';

export function LiegtBei({
  lb,
  kompakt = false,
  nurRolle = false,
  spur = false,
  onUebergeben,
}: {
  lb: LiegtBeiTyp | null;
  /** Nur Kürzel und Rolle – für dichte Listen. */
  kompakt?: boolean;
  /** Nur der Rollen-Chip – „warum liegt es bei mir" in der eigenen Liste. */
  nurRolle?: boolean;
  /**
   * Die eigene Spalte in den Vorgangslisten (v14, 17.4): Avatar und voller
   * Name, ohne Rollenzusatz.
   *
   * „Kalkulant" und „Versender" standen in jeder Zeile und beantworteten
   * eine Frage, die in der Liste niemand stellt – dort will man wissen,
   * wen man anruft, nicht warum. Die Rolle steht weiter am Vorgang und auf
   * „Mein Tag".
   */
  spur?: boolean;
  onUebergeben?: () => void;
}) {
  if (!lb) {
    return <span className="lb lb--leer">niemand · abgeschlossen</span>;
  }

  if (spur) {
    return (
      <span
        className="lb lb--spur"
        title={lb.person ? `Liegt bei ${lb.personName}` : 'Niemand zuständig'}
      >
        {/* Ohne Person bleibt der Kreis leer und gestrichelt (17.4) – der
            Strich darin wäre ein Zeichen, das niemand lesen kann. */}
        <span className={`lb-kuerzel ${lb.person ? '' : 'lb-kuerzel--fehlt'}`}>
          {lb.person ? lb.initialen : ''}
        </span>
        <span className={`lb-name lb-kurz ${lb.person ? '' : 'lb-name--fehlt'}`}>
          {lb.person ? lb.personName : 'niemand'}
        </span>
      </span>
    );
  }

  const titel = !lb.person
    ? `Dieser Schritt schuldet der ${lb.rolleLabel} – am Angebot steht keiner.` +
      (onUebergeben ? '\nKlick: jemandem ausdrücklich übergeben' : '')
    : `${lb.personName} · ${lb.rolleLabel} · ${lb.schrittLabel}` +
      (lb.uebergeben
        ? `\nÜbergeben von ${lb.uebergeben.von} am ${lb.uebergeben.amText}: ${lb.uebergeben.grund}`
        : ' · laut Zuständigkeiten') +
      (onUebergeben ? '\nKlick: an jemand anderen übergeben' : '');

  const inhalt = nurRolle ? (
    <span className={`lb-rolle ${lb.uebergeben ? 'lb-rolle--uebergeben' : ''}`}>
      {lb.uebergeben ? 'übergeben' : lb.rolleLabel}
    </span>
  ) : (
    <>
      <span className={`lb-kuerzel ${lb.person ? '' : 'lb-kuerzel--fehlt'}`}>{lb.initialen}</span>
      {!kompakt && <span className="lb-name">{lb.personName}</span>}
      <span className={`lb-rolle ${lb.uebergeben ? 'lb-rolle--uebergeben' : ''}`}>
        {lb.uebergeben ? 'übergeben' : lb.rolleLabel}
      </span>
    </>
  );

  if (onUebergeben) {
    return (
      <button
        type="button"
        className={`lb lb--knopf ${kompakt ? 'lb--kompakt' : ''}`}
        title={titel}
        onClick={(e) => {
          e.stopPropagation();
          onUebergeben();
        }}
      >
        {inhalt}
      </button>
    );
  }

  return (
    <span className={`lb ${kompakt ? 'lb--kompakt' : ''}`} title={titel}>
      {inhalt}
    </span>
  );
}
